import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Maximize2, Minimize2, Lock, Keyboard, ClipboardCopy, ArrowLeftRight } from 'lucide-react';

/**
 * Remote-control checkpoint 2 (see remote_control.md). Shows a live feed from
 * the PrimusElevator "--remote-agent" helper (a separate process from the
 * kiosk App — see the design doc) and forwards mouse/keyboard input back to
 * it. Separate component from ScreenMirrorModal.jsx (read-only, ~1fps,
 * unrelated relay) — do not conflate the two.
 *
 * SHARED BY BOTH DASHBOARDS — `primus-admin-main` (a cafe admin) and
 * `Primus-SuperAdmin-main` (an operator working remotely). This file is the
 * single source of truth for both; each app owns only a thin wrapper that
 * hands it a websocket. Requirement 5 of the doc is "available on demand to
 * admin / superadmin", and the goal statement names "the operator (from
 * anywhere)" FIRST — the operator is the case the feature exists for, since a
 * cafe admin at the counter can simply walk to the PC.
 *
 * It lives in `shared/` rather than being copied because a copy means every
 * change made twice: this component changed FIVE times on 2026-09-08 alone,
 * and the repo has already lost a session to two near-identical modals
 * (`RemoteControlModal` vs `ScreenMirrorModal`, told apart only by header
 * text). The second copy is always the one that keeps the bug.
 *
 * TWO PROPS EXIST ONLY FOR SHARING, and both matter:
 *
 *   `subscribeWs` / `sendWs` — the websocket is INJECTED rather than imported,
 *   because each app has its own `utils/wsAdmin` with its own connection and
 *   its own auth. Importing one app's directly is what made this file
 *   un-shareable before.
 *
 *   `cafeId` — a superadmin token carries no cafe, so every message must name
 *   the target cafe explicitly; `app/ws/admin.py`'s superadmin branch requires
 *   it. A cafe admin passes nothing and the backend uses their own token's
 *   cafe, ignoring the field entirely. Same shape `ScreenMirrorModal` already
 *   uses in the SuperAdmin app.
 *
 * Coordinates are un-letterboxed against the actual displayed image rect
 * (the frame is shown with objectFit:contain, so the rendered content is
 * usually smaller than the element's own box) before being normalised to
 * 0..1 and sent — the agent just scales them to its own screen size. This is
 * the fix for the letterboxing gap the read-only mirror never needed to
 * solve (it never sent coordinates anywhere).
 *
 * The caller is responsible for the start_remote_control / stop_remote_control
 * REST commands (same command pipeline the Win Lock button uses) — this
 * component only owns the WS frame subscription and input forwarding.
 */
const STALL_MS = 5000;
// How often we renew the control lease. Must stay well under the backend's
// LEASE_SECONDS (30s) so a handful of dropped renewals can't end the session.
const LEASE_RENEW_MS = 5000;

// Physical key (KeyboardEvent.code) -> Windows virtual-key code (winuser.h).
//
// Keyed on `code`, NOT `key`: `code` is the physical key's position and does
// not change with modifiers or keyboard layout, which is what a remote desktop
// wants — "the key the user actually pressed under their finger". `key` would
// hand us "l" / "L" / "¬" for that same physical key depending on what else is
// held, which is useless for building a shortcut.
const VK_BY_CODE = {
    // Ranges are generated rather than hand-listed: KeyA..KeyZ, Digit0..Digit9,
    // F1..F24 and Numpad0..Numpad9 are all contiguous on both sides, so writing
    // out ~70 entries by hand would only add room for typos.
    ...Object.fromEntries(Array.from({ length: 26 }, (_, i) => [`Key${String.fromCharCode(65 + i)}`, 0x41 + i])),
    ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [`Digit${i}`, 0x30 + i])),
    ...Object.fromEntries(Array.from({ length: 24 }, (_, i) => [`F${i + 1}`, 0x70 + i])),
    ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [`Numpad${i}`, 0x60 + i])),

    // Modifiers. Left/right are kept distinct (Windows derives the generic
    // VK_CONTROL/VK_SHIFT/VK_MENU state from these automatically).
    ControlLeft: 0xA2, ControlRight: 0xA3,
    ShiftLeft: 0xA0, ShiftRight: 0xA1,
    AltLeft: 0xA4, AltRight: 0xA5,
    MetaLeft: 0x5B, MetaRight: 0x5C,

    // Editing / whitespace.
    Enter: 0x0D, NumpadEnter: 0x0D, Tab: 0x09, Space: 0x20,
    Backspace: 0x08, Delete: 0x2E, Insert: 0x2D, Escape: 0x1B,

    // Navigation.
    ArrowLeft: 0x25, ArrowUp: 0x26, ArrowRight: 0x27, ArrowDown: 0x28,
    Home: 0x24, End: 0x23, PageUp: 0x21, PageDown: 0x22,

    // Punctuation (OEM keys — values are for a US layout, which is what the
    // cafe machines run).
    Semicolon: 0xBA, Equal: 0xBB, Comma: 0xBC, Minus: 0xBD, Period: 0xBE,
    Slash: 0xBF, Backquote: 0xC0, BracketLeft: 0xDB, Backslash: 0xDC,
    BracketRight: 0xDD, Quote: 0xDE,

    // Numpad operators.
    NumpadMultiply: 0x6A, NumpadAdd: 0x6B, NumpadSubtract: 0x6D,
    NumpadDecimal: 0x6E, NumpadDivide: 0x6F,

    // Locks and misc.
    CapsLock: 0x14, NumLock: 0x90, ScrollLock: 0x91,
    PrintScreen: 0x2C, Pause: 0x13, ContextMenu: 0x5D,
};

// Modifier VKs, as a set — used to skip auto-repeat and to force-release
// everything if focus is lost mid-chord (see releaseAllKeys).
const MODIFIER_VKS = new Set([0xA0, 0xA1, 0xA2, 0xA3, 0xA4, 0xA5, 0x5B, 0x5C]);

const toolBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
    color: '#e5e7eb', borderRadius: 8, padding: '5px 10px',
    fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
};

const RemoteControlModal = ({ pc, onClose, subscribeWs, sendWs, cafeId = null, clientId = null }) => {
    // Every message this component sends is scoped to a cafe the same way, so
    // the superadmin/cafe-admin difference lives in exactly ONE place rather
    // than being remembered at each of the four call sites below. A cafe admin
    // passes no cafeId and the backend uses their token's own cafe. The
    // window's clientId rides along too, so the server can tell windows apart.
    const scoped = useCallback(
        (payload) => {
            const withCafe = cafeId == null ? payload : { ...payload, cafe_id: cafeId };
            return clientId ? { ...withCafe, client_id: clientId } : withCafe;
        },
        [cafeId, clientId],
    );
    // Who drives when several windows have this PC open (item 28). Until the
    // server says, this window drives — also how it behaves against a backend
    // that predates shared sessions.
    const [control, setControl] = useState(null);
    const inControl = !clientId || !control?.controller || control.controller === clientId;
    const inControlRef = useRef(inControl);
    inControlRef.current = inControl;
    // What this window pressed while driving, so losing the wheel releases it.
    const pressedRef = useRef({ keys: new Set(), mouse: null });
    const hadControlRef = useRef(false);
    const watchers = (control?.participants || [])
        .filter((p) => p.client_id !== clientId)
        .map((p) => p.name || 'Someone');
    const driveBtn = inControl ? toolBtn : { ...toolBtn, opacity: 0.4, cursor: 'not-allowed' };
    const [frame, setFrame] = useState(null);
    const [stalled, setStalled] = useState(false);
    const [everReceived, setEverReceived] = useState(false);
    // A ring drawn where you just clicked. Frames arrive ~3x a second, so without
    // this you can wait a third of a second to see any sign a click registered —
    // long enough to assume it didn't and click again.
    const [clickPulse, setClickPulse] = useState(null);
    const pulseTimer = useRef(null);
    const lastFrameAt = useRef(0);
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    const lastMoveSentAt = useRef(0);
    // VKs we've sent a key_down for and not yet released. Doubles as the
    // source of truth for key_up (so we can never emit an unmatched release)
    // and as the list to force-release when focus is lost.
    const heldKeys = useRef(new Set());
    // Last clipboard text we pushed to the remote machine — see handlePaste.
    const lastPushedClip = useRef(null);
    const pasteFallback = useRef(null);
    const cardRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [kbLocked, setKbLocked] = useState(false);
    // Whether the CUSTOMER is currently being shown the on-screen notice.
    // null = no frame carrying the field has arrived yet, which is deliberately
    // distinct from false: "we don't know yet" and "they are NOT being told"
    // must not look the same to the admin.
    const [customerNotified, setCustomerNotified] = useState(null);
    // Short-lived note about the last copy FROM the remote machine
    // ({ tone, text }), cleared by a timer. This is the only feedback that a
    // Ctrl+C did anything: the copy happens on a machine the admin is looking
    // at through a JPEG, and their own clipboard gives no sign of having
    // changed, so without this a working copy and a failed one look identical.
    const [clipNote, setClipNote] = useState(null);
    const clipNoteTimer = useRef(null);

    const showClipNote = useCallback((tone, text) => {
        setClipNote({ tone, text });
        if (clipNoteTimer.current) clearTimeout(clipNoteTimer.current);
        clipNoteTimer.current = setTimeout(() => { clipNoteTimer.current = null; setClipNote(null); }, 2500);
    }, []);

    useEffect(() => () => { if (clipNoteTimer.current) clearTimeout(clipNoteTimer.current); }, []);

    // The remote machine's clipboard, arriving because the admin asked for it.
    const receiveRemoteClipboard = useCallback((p) => {
        if (p.oversize_chars) {
            showClipNote('warn', `Too large to copy (${Math.round(p.oversize_chars / 1000)}k characters)`);
            return;
        }
        // "Nothing NEW was copied" — the copy keystroke reached the PC but its
        // clipboard never changed, so there was no selection or the app ignored
        // the chord. Still distinct from an empty clipboard: one means "your
        // copy did not take", the other "there was nothing to take".
        //
        // A "Copy from PC" button used to sit in the toolbar and fetch the
        // clipboard AS IT STANDS, which covered what Ctrl+C structurally cannot
        // see: a right-click Copy, an app's own Copy button, or something
        // already on that clipboard before the session began. Removed by
        // decision on 2026-09-09 once Ctrl+C was working. Noted because if
        // those cases are ever reported as "I copied it and nothing happened",
        // this is the reason — and the agent still accepts
        // `request_clipboard` with after_copy:false, so restoring it is a
        // button rather than a feature.
        if (p.unchanged) {
            showClipNote('warn', 'Nothing new was copied');
            return;
        }
        const text = p.text;
        if (!text) { showClipNote('warn', 'Nothing was copied'); return; }

        // Remember it as the last thing the two clipboards agreed on. Without
        // this the very next Ctrl+V would push it straight back to the remote
        // machine as though the admin had copied it locally — harmless, but it
        // also means handlePaste's "has the laptop clipboard changed?" test
        // starts telling the truth, which is what removed the old first-paste
        // guesswork (item 12).
        lastPushedClip.current = text;

        // writeText can legitimately refuse — an unfocused document, a browser
        // that wants a fresh user gesture, or a policy that blocks clipboard
        // writes. Say so rather than leaving the admin to discover it by
        // pasting the wrong thing somewhere else.
        //
        // The API can also be ABSENT: navigator.clipboard is undefined outside a
        // secure context, so anyone running the dashboard over plain http has no
        // object to call. Checked rather than optional-chained into a .then(),
        // which would throw on undefined and take the whole message handler —
        // and with it the frame stream — down with it.
        const write = navigator.clipboard?.writeText?.(text);
        if (!write?.then) {
            showClipNote('warn', 'This browser will not allow clipboard writes here');
            return;
        }
        write
            .then(() => showClipNote('ok', `Copied ${text.length} characters from the PC`))
            .catch(() => showClipNote('warn', 'Your browser blocked the clipboard write'));
    }, [showClipNote]);

    useEffect(() => {
        if (!pc?.id) return;
        let alive = true;

        const unsub = subscribeWs((msg) => {
            // Every admin websocket message lands here, not just ours — PC
            // tiles, chat, order alerts — so filter on the event FIRST and
            // leave everything else untouched.
            if (!alive || !msg) return;
            if (msg.event !== 'remote.frame' && msg.event !== 'remote.clipboard' && msg.event !== 'remote.control') return;
            const p = msg.payload || {};
            if (p.pc_id !== pc.id) return;

            if (msg.event === 'remote.control') {
                setControl(p);
                return;
            }

            if (msg.event === 'remote.clipboard') {
                // Only the driver can ask for it; a watcher must not have the
                // driver's copy land on its own clipboard.
                if (inControlRef.current) receiveRemoteClipboard(p);
                return;
            }

            if (!p.data) return;
            lastFrameAt.current = Date.now();
            setEverReceived(true);
            setStalled(false);
            setCustomerNotified(!!p.notice);
            setFrame(`data:image/jpeg;base64,${p.data}`);
        });

        // remote.subscribe is a RENEWING LEASE, not a one-off registration.
        // Each one pushes the kiosk agent's capture lease forward; stop sending
        // them -- this tab closed, the browser crashed, the laptop slept -- and
        // the agent stops capturing and ends the session by itself, instead of
        // being left driving an unattended machine (remote_control.md §102).
        // Renewal interval must stay comfortably under the backend's
        // LEASE_SECONDS: the injected send() drops silently when the socket is
        // not OPEN (§153),
        // so single renewals WILL be missed across a reconnect.
        const renew = () => sendWs({ event: 'remote.subscribe', payload: scoped({ pc_id: pc.id }) });
        renew();
        const leaseId = setInterval(renew, LEASE_RENEW_MS);

        const stallId = setInterval(() => {
            if (lastFrameAt.current && Date.now() - lastFrameAt.current > STALL_MS) {
                setStalled(true);
            }
        }, 1000);

        return () => {
            alive = false;
            clearInterval(leaseId);
            clearInterval(stallId);
            try { sendWs({ event: 'remote.unsubscribe', payload: scoped({ pc_id: pc.id }) }); } catch { /* ignore */ }
            try { unsub && unsub(); } catch { /* ignore */ }
        };
    }, [pc?.id, cafeId]);

    // Un-letterbox a client (viewport) point against the <img>'s actual
    // rendered content rect (objectFit:contain can pillarbox/letterbox),
    // returning normalised 0..1 floats, or null if the point falls in the
    // letterbox padding rather than on the image itself.
    const toNormalized = useCallback((clientX, clientY) => {
        const img = imgRef.current;
        if (!img || !img.naturalWidth || !img.naturalHeight) return null;
        const rect = img.getBoundingClientRect();
        const scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
        const dispW = img.naturalWidth * scale;
        const dispH = img.naturalHeight * scale;
        const offsetX = (rect.width - dispW) / 2;
        const offsetY = (rect.height - dispH) / 2;
        const x = clientX - rect.left - offsetX;
        const y = clientY - rect.top - offsetY;
        if (x < 0 || y < 0 || x > dispW || y > dispH) return null;
        return { x: x / dispW, y: y / dispH };
    }, []);

    // Returns whether it actually went out. wsAdmin's send() reports this and we
    // used to throw the answer away, which is what made releasing keys unsafe
    // (see releaseAllKeys). While someone else drives, only releases of what
    // THIS window pressed go out; the server enforces the same rule.
    const sendInput = useCallback((payload) => {
        if (!pc?.id) return false;
        const pressed = pressedRef.current;
        if (!inControlRef.current) {
            if (payload.type === 'key_up' && pressed.keys.has(payload.key)) pressed.keys.delete(payload.key);
            else if (payload.type === 'mouse_up' && pressed.mouse) pressed.mouse = null;
            else return false;
        } else if (payload.type === 'key_down') pressed.keys.add(payload.key);
        else if (payload.type === 'key_up') pressed.keys.delete(payload.key);
        else if (payload.type === 'mouse_down') pressed.mouse = payload.button || 'left';
        else if (payload.type === 'mouse_up') pressed.mouse = null;
        return sendWs({ event: 'remote.input', payload: scoped({ pc_id: pc.id, ...payload }) }) !== false;
    }, [pc?.id, scoped, sendWs]);

    // Losing the wheel mid-keypress lets go of everything, or the customer's
    // own keyboard would inherit a key held down.
    useEffect(() => {
        if (inControl) {
            if (control) hadControlRef.current = true;
            return;
        }
        const pressed = pressedRef.current;
        for (const vk of [...pressed.keys]) sendInput({ type: 'key_up', key: vk });
        if (pressed.mouse) sendInput({ type: 'mouse_up', button: pressed.mouse });
        heldKeys.current.clear();
    }, [inControl, control, sendInput]);

    // Asking for control (item 28). The driver has to accept; after 30s with no
    // answer the server hands over anyway, so nobody waits on a window whose
    // person has walked away (user decision, 2026-09-15).
    const sendHandover = useCallback((event) => {
        if (!pc?.id) return;
        sendWs({ event, payload: scoped({ pc_id: pc.id }) });
    }, [pc?.id, scoped, sendWs]);
    const askForControl = useCallback(() => {
        sendHandover('remote.request');
        containerRef.current?.focus();
    }, [sendHandover]);
    const acceptRequest = useCallback(() => sendHandover('remote.accept'), [sendHandover]);
    const declineRequest = useCallback(() => sendHandover('remote.decline'), [sendHandover]);
    const cancelRequest = useCallback(() => sendHandover('remote.cancel'), [sendHandover]);

    const pendingRequest = control?.request || null;
    const iAsked = !!pendingRequest && pendingRequest.client_id === clientId;
    const requestForMe = !!control && inControl && !!pendingRequest && !iAsked;
    // Fixed in render when the message arrives, not in an effect: an effect left
    // one render with the request but no deadline, and the claim fired in that gap.
    const requestDeadline = React.useMemo(
        () => (pendingRequest ? Date.now() + (pendingRequest.remaining_ms ?? 30000) : null),
        [control],
    );
    const [, setTick] = useState(0);
    const [declinedBy, setDeclinedBy] = useState(null);
    useEffect(() => {
        if (control?.declined?.client_id === clientId) setDeclinedBy(control.declined.by || 'The driver');
    }, [control]);
    useEffect(() => {
        if (!declinedBy) return undefined;
        const t = setTimeout(() => setDeclinedBy(null), 6000);
        return () => clearTimeout(t);
    }, [declinedBy]);
    useEffect(() => {
        if (requestDeadline == null) return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [requestDeadline]);
    const secondsLeft = requestDeadline == null ? 0 : Math.max(0, Math.ceil((requestDeadline - Date.now()) / 1000));

    // Time's up and still waiting: claim now rather than on the next renewal.
    const claimedRef = useRef(null);
    useEffect(() => {
        if (!iAsked || secondsLeft > 0 || claimedRef.current === requestDeadline) return;
        claimedRef.current = requestDeadline;
        sendHandover('remote.request');
    }, [iAsked, secondsLeft, requestDeadline, sendHandover]);

    // A request must be impossible to miss: a beep once, and a flashing tab
    // title while it waits. The prompt itself is drawn over the screen.
    const alertedForRef = useRef(null);
    useEffect(() => {
        if (!requestForMe) {
            alertedForRef.current = null;
            return undefined;
        }
        if (alertedForRef.current !== pendingRequest.client_id) {
            alertedForRef.current = pendingRequest.client_id;
            try {
                const audio = new (window.AudioContext || window.webkitAudioContext)();
                [0, 0.25].forEach((at) => {
                    const osc = audio.createOscillator();
                    const gain = audio.createGain();
                    osc.frequency.value = 880;
                    gain.gain.value = 0.2;
                    osc.connect(gain).connect(audio.destination);
                    osc.start(audio.currentTime + at);
                    osc.stop(audio.currentTime + at + 0.15);
                });
                setTimeout(() => audio.close(), 1000);
            } catch { /* no audio: the prompt still shows */ }
        }
        const original = document.title;
        const id = setInterval(() => {
            document.title = document.title === original ? 'Control requested — accept?' : original;
        }, 1000);
        return () => {
            clearInterval(id);
            document.title = original;
        };
    }, [requestForMe, pendingRequest?.client_id]);

    const handleMouseMove = useCallback((e) => {
        const now = Date.now();
        if (now - lastMoveSentAt.current < 50) return; // light throttle
        const p = toNormalized(e.clientX, e.clientY);
        if (!p) return;
        lastMoveSentAt.current = now;
        sendInput({ type: 'mouse_move', x: p.x, y: p.y });
    }, [toNormalized, sendInput]);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        // Focus BEFORE the early return below. preventDefault suppresses the
        // browser's focus-on-mousedown, so this call is the only thing that gives
        // the container keyboard focus -- and clicks that land in the letterbox
        // padding around a non-16:9 remote screen return early. Doing it after the
        // return meant clicking those bars reproduced the exact bug this line was
        // added to fix: mouse works, keyboard silently does nothing.
        containerRef.current?.focus();
        // Watching: no click goes out, so no ring suggesting one did.
        if (!inControlRef.current) return;

        const p = toNormalized(e.clientX, e.clientY);
        if (!p) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
            if (pulseTimer.current) clearTimeout(pulseTimer.current);
            setClickPulse({ x: e.clientX - rect.left, y: e.clientY - rect.top, id: Date.now() });
            pulseTimer.current = setTimeout(() => setClickPulse(null), 400);
        }

        sendInput({ type: 'mouse_down', x: p.x, y: p.y, button: e.button === 2 ? 'right' : 'left' });
    }, [toNormalized, sendInput]);

    useEffect(() => () => {
        if (pulseTimer.current) clearTimeout(pulseTimer.current);
        if (pasteFallback.current) clearTimeout(pasteFallback.current);
    }, []);

    const handleMouseUp = useCallback((e) => {
        sendInput({ type: 'mouse_up', button: e.button === 2 ? 'right' : 'left' });
    }, [sendInput]);

    // Release every key we still believe is held. Without this, tabbing or
    // clicking away mid-chord (very easy to do — Alt+Tab is itself a chord)
    // strands the modifier DOWN on the remote machine forever, and every
    // keystroke after that is silently interpreted as Ctrl+<key>.
    //
    // KEEP the ones we could not actually send. wsAdmin's send() drops silently
    // when the socket is not OPEN, so clearing unconditionally meant that in the
    // one situation this exists for -- a connection that has just died -- we
    // forgot the keys precisely when nothing had released them. They stay in the
    // set and go out on the next attempt once the socket is back.
    //
    // This is best-effort by nature: a browser that has crashed or slept cannot
    // send anything at all. The real guarantee is agent-side, which releases
    // held keys when its own connection drops -- it is the only party still
    // running when this end has gone.
    const releaseAllKeys = useCallback(() => {
        const stillHeld = new Set();
        for (const vk of heldKeys.current) {
            if (!sendInput({ type: 'key_up', key: vk })) stillHeld.add(vk);
        }
        heldKeys.current = stillHeld;
    }, [sendInput]);

    // Same guarantee on unmount: closing the modal mid-chord must not leave a
    // modifier stuck down on the remote machine.
    useEffect(() => releaseAllKeys, [releaseAllKeys]);

    // Wheel, as a NATIVE listener rather than React's onWheel: React registers
    // wheel handlers passively, so preventDefault() inside onWheel is ignored and
    // the dashboard page behind the modal scrolls along with the remote machine.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const onWheel = (e) => {
            e.preventDefault();
            const p = toNormalized(e.clientX, e.clientY);
            if (!p) return;

            // Browsers report wheel movement in pixels, lines or pages depending on
            // the device; Windows wants multiples of WHEEL_DELTA (120). Normalise to
            // pixels first, then to notches, and never round a real scroll down to
            // zero — a small trackpad nudge should still move something.
            const notches = (raw, mode) => {
                if (!raw) return 0;
                const px = mode === 1 ? raw * 16 : mode === 2 ? raw * 400 : raw;
                const n = Math.round(px / 100);
                return (n === 0 ? Math.sign(px) : n) * 120;
            };

            sendInput({
                type: 'scroll',
                x: p.x,
                y: p.y,
                // Browsers count down as positive; Windows counts up as positive.
                dy: -notches(e.deltaY, e.deltaMode),
                dx: notches(e.deltaX, e.deltaMode),
            });
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [toNormalized, sendInput]);

    // Paste: put the admin's own clipboard on the remote machine, then paste it.
    //
    // Driven by the browser's paste event rather than reading the clipboard
    // directly, because the event hands us the contents with no permission prompt
    // — the user pressing Ctrl+V IS the consent.
    const sendCtrlV = useCallback(() => {
        sendInput({ type: 'key_down', key: 0xA2 }); // LCtrl
        sendInput({ type: 'key_down', key: 0x56 }); // V
        sendInput({ type: 'key_up', key: 0x56 });
        sendInput({ type: 'key_up', key: 0xA2 });
    }, [sendInput]);

    const handlePaste = useCallback((e) => {
        // A paste arrived, so the fallback below is not needed.
        if (pasteFallback.current) { clearTimeout(pasteFallback.current); pasteFallback.current = null; }

        const text = e.clipboardData?.getData('text/plain');
        if (!text) { sendCtrlV(); return; }
        e.preventDefault();

        // Only push when this laptop's clipboard has actually CHANGED since we last
        // pushed. Otherwise copying something ON the remote machine and pasting it
        // there would be silently overwritten by whatever is still sitting on the
        // admin's laptop — copy-paste within the remote would quietly stop working.
        // Unchanged clipboard means "they didn't copy anything new here", so leave
        // the remote's own clipboard alone and just send the keystroke.
        if (text !== lastPushedClip.current) {
            lastPushedClip.current = text;
            sendInput({ type: 'set_clipboard', text });
        }

        // Ordering is safe: this goes down the same connection, and the agent
        // processes messages in order, so the clipboard is set before the paste.
        sendCtrlV();
    }, [sendInput, sendCtrlV]);

    // A button because Brave/Firefox/Safari never hand Alt+Tab to a page; Ctrl+Alt+Tab keeps the switcher open to click from.
    const openWindowSwitcher = useCallback(() => {
        sendInput({ type: 'key_down', key: 0xA2 }); // LCtrl
        sendInput({ type: 'key_down', key: 0xA4 }); // LAlt
        sendInput({ type: 'key_down', key: 0x09 }); // Tab
        sendInput({ type: 'key_up', key: 0x09 });
        sendInput({ type: 'key_up', key: 0xA4 });
        sendInput({ type: 'key_up', key: 0xA2 });
        containerRef.current?.focus();
    }, [sendInput]);

    // Fullscreen exists to unlock the KEYBOARD, not for a bigger picture.
    //
    // Alt+Tab, Escape and Ctrl+W never reach a web page: the browser and Windows
    // take them first, so they act on the ADMIN's machine instead of the remote
    // one. The Keyboard Lock API is the sanctioned way to claim them, and the
    // browser only grants it in fullscreen — the same mechanism Chrome's own
    // remote desktop uses. Chrome and Edge only: Brave, Firefox and Safari don't
    // offer it, so there those keys stay on the local machine.
    const enterFullscreen = useCallback(async () => {
        const el = cardRef.current;
        if (!el?.requestFullscreen) return;
        try {
            await el.requestFullscreen();
        } catch {
            return; // denied — nothing else to do
        }
        try {
            if (navigator.keyboard?.lock) {
                await navigator.keyboard.lock(); // no argument = every key we can have
                setKbLocked(true);
            }
        } catch {
            setKbLocked(false); // fullscreen without the keys is still useful
        }
        containerRef.current?.focus();
    }, []);

    const exitFullscreen = useCallback(async () => {
        try { navigator.keyboard?.unlock?.(); } catch { /* ignore */ }
        setKbLocked(false);
        try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* ignore */ }
    }, []);

    // The user can leave fullscreen without our buttons (holding Escape), so the
    // browser is the source of truth for this, not our click handlers.
    useEffect(() => {
        const onChange = () => {
            const on = Boolean(document.fullscreenElement);
            setIsFullscreen(on);
            if (!on) {
                try { navigator.keyboard?.unlock?.(); } catch { /* ignore */ }
                setKbLocked(false);
            }
        };
        document.addEventListener('fullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            try { navigator.keyboard?.unlock?.(); } catch { /* ignore */ }
        };
    }, []);

    // Keys the browser will never hand over, however we ask — they go as an
    // instruction to the agent instead of as keystrokes. Ctrl+Alt+Del is blocked
    // by Windows itself so that a login screen cannot be faked; Win+L is taken by
    // the admin's own OS before the page sees it. Every remote-desktop tool ends
    // up offering these as buttons for exactly this reason.
    const sendCommand = useCallback((type) => {
        sendInput({ type });
        containerRef.current?.focus(); // clicking the button stole focus; give it back
    }, [sendInput]);

    const handleKeyDown = useCallback((e) => {
        // Ctrl+V is the ONE key we must not preventDefault on. The browser's paste
        // event IS the default action of this chord, so cancelling it here stops
        // handlePaste ever running — which is exactly what happened: the clipboard
        // was never read, never sent, and the agent logged nothing at all because
        // no message reached it. Same shape of mistake as the original focus bug,
        // where preventDefault also suppressed something still needed.
        const isPasteChord = (e.ctrlKey || e.metaKey) && e.code === 'KeyV';
        if (!isPasteChord) e.preventDefault();
        e.stopPropagation();

        // Ctrl+V is handled by handlePaste, which fires straight after this and
        // knows what is on the clipboard; forwarding here too would paste twice.
        //
        // The timer is a safety net, not decoration. If the paste event never
        // arrives — an empty clipboard, or a browser that declines to fire it on a
        // non-editable element — swallowing the keystroke outright would turn
        // Ctrl+V from "pastes the remote's clipboard" into "does nothing", which is
        // worse than the behaviour we started with. So send the plain keystroke if
        // no paste shows up almost immediately.
        if (isPasteChord) {
            if (pasteFallback.current) clearTimeout(pasteFallback.current);
            pasteFallback.current = setTimeout(() => { pasteFallback.current = null; sendCtrlV(); }, 60);
            return;
        }

        // The other direction: a copy chord asks for the remote clipboard back
        // once the copy has landed, so the admin can paste it on their own
        // laptop. Ctrl+X counts — a cut is a copy that also deletes — and
        // Ctrl+Insert is the same command on the keys that predate Ctrl+C.
        //
        // Sent BEFORE the keystroke is forwarded, and that ordering is the whole
        // mechanism, not a detail. The agent samples the clipboard's sequence
        // number the moment this arrives and then waits for it to CHANGE; since
        // messages are processed in order, sampling happens strictly before the
        // copy it is waiting for. Send it afterwards and the agent may sample a
        // clipboard the copy has already updated, then wait out its timeout for
        // a change that has been and gone.
        //
        // Nothing is preventDefault-exempt here: the browser's own copy is
        // suppressed like every other chord, which is what leaves the admin's
        // clipboard free for us to fill with the remote's contents rather than
        // with whatever happened to be selected on the dashboard page.
        if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyC' || e.code === 'KeyX' || e.code === 'Insert')) {
            sendInput({ type: 'request_clipboard', after_copy: true });
        }

        const vk = VK_BY_CODE[e.code];

        // Ctrl/Alt/Meta held means this keystroke is a COMMAND, not typing.
        // Shift deliberately doesn't count — Shift+A is just a capital A.
        const isShortcut = e.ctrlKey || e.altKey || e.metaKey;

        // Plain typing keeps going through Unicode injection: it reproduces the
        // character the admin actually sees on their own keyboard regardless of
        // either machine's layout, and it's the path already proven to work for
        // password entry on the login screen. VK codes would re-introduce
        // layout dependence for no gain here.
        //
        // Space is the exception: its `key` is a single character so it would
        // qualify, but sending it as text only ever types a space, while the
        // real key also activates whatever button has focus — which is half of
        // what Space is for on a login screen or a dialog.
        if (!isShortcut && !MODIFIER_VKS.has(vk) && e.key.length === 1 && e.code !== 'Space') {
            sendInput({ type: 'type_text', text: e.key });
            return;
        }

        if (vk === undefined) return;
        // Held modifiers auto-repeat; forwarding the repeats is pure noise.
        if (e.repeat && MODIFIER_VKS.has(vk)) return;

        heldKeys.current.add(vk);
        sendInput({ type: 'key_down', key: vk });
    }, [sendInput]);

    const handleKeyUp = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const vk = VK_BY_CODE[e.code];
        // Only release what we actually pressed: a keystroke that went out as
        // type_text has no VK to release.
        if (vk === undefined || !heldKeys.current.delete(vk)) return;
        sendInput({ type: 'key_up', key: vk });
    }, [sendInput]);

    if (!pc) return null;

    return (
        <div
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
            onClick={onClose}
        >
            <style>{`@keyframes rcClickPulse {
                from { transform: scale(0.4); opacity: 1; }
                to   { transform: scale(1.6); opacity: 0; }
            }`}</style>
            <div
                ref={cardRef}
                style={{
                    background: '#0b0f17', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: isFullscreen ? 0 : 16,
                    width: '100%', maxWidth: isFullscreen ? 'none' : 1100,
                    // Always a column that fits the viewport. Previously the card
                    // was sized by a fixed 16:9 picture, so on a 1366x768 screen it
                    // stood ~714px tall in ~570px of space and, being centred,
                    // overflowed equally top and bottom -- silently clipping the
                    // header (and footer) right off. That was survivable while the
                    // header was only a title; it is not now that the controls live
                    // there. The picture gives up height instead.
                    // Fill the available height rather than shrink-wrapping. With no
                    // aspect ratio on the frame nothing claims height any more, so a
                    // content-sized card collapsed the picture to its 200px floor --
                    // a postage stamp. Height and max-height together mean it always
                    // fills the window and never exceeds it.
                    height: isFullscreen ? '100vh' : 'calc(100vh - 48px)',
                    maxHeight: isFullscreen ? '100vh' : 'calc(100vh - 48px)',
                    display: 'flex', flexDirection: 'column',
                    color: '#fff', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: stalled ? '#f59e0b' : '#22c55e', boxShadow: stalled ? 'none' : '0 0 8px #22c55e' }} />
                        <span style={{ fontWeight: 700, fontSize: 16 }}>Remote Control · {pc.name}</span>
                        {inControl ? (
                            <span style={{ fontSize: 12, color: kbLocked ? '#22c55e' : '#9CA3AF' }}>
                                {kbLocked
                                    ? 'all keys captured — Alt+Tab and Escape go to the remote PC'
                                    : 'click to control · type to send keys'}
                            </span>
                        ) : (
                            <span style={{ fontSize: 12, color: '#fbbf24' }}>
                                {control?.controller_name || 'Someone else'} is in control — you are watching
                            </span>
                        )}
                        {inControl && watchers.length > 0 && (
                            <span style={{ fontSize: 12, color: '#93c5fd' }}>
                                {watchers.join(', ')} watching
                            </span>
                        )}
                        {/*
                          Whether the person at the machine can see that this is
                          happening. Driven by the agent checking its own notice
                          window on every frame, not by us assuming it worked —
                          so if the notice dies mid-session this stops claiming
                          otherwise within a third of a second.

                          The warning state is the point of the whole indicator:
                          controlling someone's PC while they have no idea is the
                          situation an admin must never be in unknowingly. Amber,
                          not silent.
                        */}
                        {customerNotified !== null && (
                            <span
                                title={customerNotified
                                    ? 'The customer can see an on-screen notice that staff are controlling this PC.'
                                    : 'WARNING: the on-screen notice is NOT visible on the remote screen. '
                                      + 'The customer may not know this is happening. A fullscreen game can cover it.'}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    fontSize: 12, padding: '2px 8px', borderRadius: 999,
                                    color: customerNotified ? '#93c5fd' : '#fbbf24',
                                    background: customerNotified ? 'rgba(59,130,246,0.14)' : 'rgba(245,158,11,0.16)',
                                    border: `1px solid ${customerNotified ? 'rgba(59,130,246,0.4)' : 'rgba(245,158,11,0.5)'}`,
                                }}
                            >
                                {/* Blue dot, matching the badge the customer is
                                    actually looking at on the kiosk. */}
                                <span style={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    background: customerNotified ? '#3b82f6' : '#f59e0b',
                                }} />
                                {customerNotified ? 'Customer notified' : 'Customer NOT notified'}
                            </span>
                        )}
                        {/*
                          The only sign a copy FROM the remote machine worked.
                          The admin is watching that PC through a JPEG and their
                          own clipboard gives no outward sign of changing, so
                          without this a successful copy, a copy that found
                          nothing, and a browser that refused the write all look
                          exactly the same: nothing happens.
                        */}
                        {clipNote && (
                            <span style={{
                                fontSize: 12, padding: '2px 8px', borderRadius: 999,
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: clipNote.tone === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                                color: clipNote.tone === 'ok' ? '#22c55e' : '#f59e0b',
                            }}>
                                <ClipboardCopy size={12} /> {clipNote.text}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {!inControl && (iAsked ? (
                            <>
                                <span style={{ fontSize: 12, color: '#fbbf24' }}>
                                    Waiting for {control?.controller_name || 'the driver'} to accept — hands over in {secondsLeft}s
                                </span>
                                <button type="button" onClick={cancelRequest} style={toolBtn}>Cancel</button>
                            </>
                        ) : (
                            <button
                                type="button" onClick={askForControl} disabled={!!pendingRequest}
                                title={pendingRequest
                                    ? `${pendingRequest.name || 'Someone'} has already asked for control`
                                    : 'Ask the person driving to hand over the mouse and keyboard'}
                                style={{ ...toolBtn, background: 'rgba(245,158,11,0.18)', border: '1px solid rgba(245,158,11,0.6)', color: '#fbbf24', opacity: pendingRequest ? 0.5 : 1 }}
                            >
                                {hadControlRef.current ? 'Ask to take back' : 'Ask for control'}
                            </button>
                        ))}
                        {declinedBy && !inControl && (
                            <span style={{ fontSize: 12, color: '#f87171' }}>{declinedBy} declined</span>
                        )}
                        {requestForMe && (
                            <div
                                role="alertdialog" aria-live="assertive" aria-label="Control requested"
                                style={{ position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <style>{`@keyframes rcRequestPulse {
                                    0%, 100% { box-shadow: 0 0 0 4px rgba(245,158,11,0.3), 0 20px 60px rgba(0,0,0,0.7); }
                                    50% { box-shadow: 0 0 0 16px rgba(245,158,11,0.5), 0 20px 60px rgba(0,0,0,0.7); }
                                }`}</style>
                                <div style={{ background: '#1f1405', border: '3px solid #f59e0b', borderRadius: 16, padding: '28px 32px', maxWidth: 480, textAlign: 'center', color: '#fff', animation: 'rcRequestPulse 1.2s ease-in-out infinite' }}>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: '#fbbf24', marginBottom: 10 }}>
                                        {pendingRequest.name || 'Someone'} is asking for control
                                    </div>
                                    <div style={{ fontSize: 15, color: '#e5e7eb', marginBottom: 22, lineHeight: 1.5 }}>
                                        They will get the mouse and keyboard, and you keep watching.
                                        <br />Hands over automatically in <b style={{ color: '#fbbf24', fontSize: 20 }}>{secondsLeft}s</b>
                                    </div>
                                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                        <button type="button" onClick={acceptRequest} style={{ ...toolBtn, fontSize: 15, padding: '10px 24px', background: '#16a34a', border: '1px solid #22c55e', color: '#fff' }}>Accept</button>
                                        <button type="button" onClick={declineRequest} style={{ ...toolBtn, fontSize: 15, padding: '10px 24px' }}>Decline</button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <button
                            type="button" onClick={() => sendCommand('sas')} disabled={!inControl}
                            title="Send Ctrl+Alt+Delete — the browser can never forward this key itself"
                            style={driveBtn}
                        >
                            <Keyboard size={14} /> Ctrl+Alt+Del
                        </button>
                        <button
                            type="button" onClick={openWindowSwitcher} disabled={!inControl}
                            title="Show the open windows on the remote PC, then click the one you want — works in any browser"
                            style={driveBtn}
                        >
                            <ArrowLeftRight size={14} /> Switch tabs
                        </button>
                        <button
                            type="button" onClick={() => sendCommand('lock_workstation')} disabled={!inControl}
                            title="Lock this PC — pressing Win+L would lock YOUR machine instead"
                            style={driveBtn}
                        >
                            <Lock size={14} /> Lock PC
                        </button>
                        <button
                            type="button"
                            onClick={() => (isFullscreen ? exitFullscreen() : enterFullscreen())}
                            title={isFullscreen
                                ? 'Leave fullscreen (Alt+Tab and Escape go back to your own machine)'
                                : 'Fullscreen — lets Alt+Tab, Escape and Ctrl+W reach the remote PC'}
                            style={toolBtn}
                        >
                            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                            {isFullscreen ? 'Exit' : 'Fullscreen'}
                        </button>
                        <button
                            type="button" onClick={onClose} title="Stop remote control"
                            style={{ background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 4, marginLeft: 4 }}
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                <div
                    ref={containerRef}
                    tabIndex={0}
                    onMouseMove={handleMouseMove}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onContextMenu={(e) => e.preventDefault()}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    onPaste={handlePaste}
                    onBlur={releaseAllKeys}
                    style={{
                        position: 'relative', width: '100%', background: '#000',
                        // No fixed aspect ratio: the <img> already letterboxes
                        // itself with objectFit:contain, so the box just takes
                        // whatever height is left after the header and footer.
                        flex: 1, minHeight: 200,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'default', outline: 'none',
                    }}
                >
                    {frame ? (
                        <img ref={imgRef} src={frame} alt={`Screen of ${pc.name}`} draggable={false} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', pointerEvents: 'none' }} />
                    ) : (
                        <div style={{ color: '#9CA3AF', fontSize: 14 }}>
                            {pc.online === false ? 'PC is offline.' : 'Waiting for the agent to connect…'}
                        </div>
                    )}

                    {clickPulse && (
                        <span
                            key={clickPulse.id}
                            style={{
                                position: 'absolute', left: clickPulse.x, top: clickPulse.y,
                                width: 26, height: 26, marginLeft: -13, marginTop: -13,
                                border: '2px solid #22c55e', borderRadius: '50%',
                                pointerEvents: 'none',
                                animation: 'rcClickPulse 400ms ease-out forwards',
                            }}
                        />
                    )}

                    {stalled && everReceived && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', color: '#fcd34d', fontSize: 14 }}>
                            Frames paused — reconnecting…
                        </div>
                    )}
                </div>

                <div style={{ padding: '10px 18px', fontSize: 12, color: '#9CA3AF', borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
                    Unlocked desktop only, ~5fps, no smoothing. Keyboard shortcuts (Ctrl/Alt/Shift combos) are
                    forwarded; Ctrl+Alt+Del and Win+L can&apos;t be — the browser never sees them. Closing this ends the session.
                </div>
            </div>
        </div>
    );
};

export default RemoteControlModal;
